'use client';

import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Player } from '@remotion/player';
import {
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
  Sequence,
  AbsoluteFill,
  Series,
  random,
  Audio,
  Img,
  OffthreadVideo,
} from 'remotion';
import * as Babel from '@babel/standalone';
import { ArrowsOut, X, Gauge, Download, Spinner, ArrowClockwise } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { normalizeRemotionComponentCode } from '@/lib/remotion-code-normalizer';
import { VideoKit } from '@/remotion/video-kit';

/**
 * Format a duration as MM:SS or seconds with one decimal, whichever fits.
 * Short clips: "3.5s". Longer: "1:47" or "12:34". No ugly 107.46666...s.
 */
function formatDuration(durationInFrames: number, fps: number): string {
  const totalSeconds = durationInFrames / fps;
  if (totalSeconds < 60) {
    return `${totalSeconds.toFixed(1)}s`;
  }
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.round(totalSeconds - minutes * 60);
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}
import { useProductTelemetryOnce } from '@/hooks/use-product-telemetry-once';
import { captureProductTelemetry } from '@/lib/product-telemetry';

const SPEED_OPTIONS = [0.25, 0.5, 1, 1.5, 2] as const;
import React from 'react';

interface RemotionRendererProps {
  title: string;
  description?: string;
  componentCode: string;
  durationInFrames: number;
  fps: number;
  width: number;
  height: number;
  visualizationId?: string;
  displayContext?: 'inline' | 'artifact';
  renderActions?: React.ReactNode;
  onRetryError?: (info: { toolName: string; error: string; code: string }) => void;
}

// Create a dynamic component from code string
function createDynamicComponent(code: string): React.FC | null {
  try {
    const normalizedCode = normalizeRemotionComponentCode(code);
    // The AI provides the complete component body.
    // We just wrap it in a function definition.
    const componentCode = `
function DynamicComponent() {
  ${normalizedCode}
}
    `;

    // Transpile JSX to JS
    const transpiledCode = Babel.transform(componentCode, {
      presets: ['react', 'typescript'],
      filename: 'component.tsx',
    }).code;

    if (!transpiledCode) {
      throw new Error('Failed to transpile code');
    }

    // Create the component using Function constructor
    // We pass in all the Remotion utilities and React as parameters
    const componentFactory = new Function(
      'React',
      'useCurrentFrame',
      'useVideoConfig',
      'interpolate',
      'spring',
      'Sequence',
      'AbsoluteFill',
      'Series',
      'random',
      'Audio',
      'Img',
      'OffthreadVideo',
      'VideoKit',
      `${transpiledCode}; return DynamicComponent;`
    );

    return componentFactory(
      React,
      useCurrentFrame,
      useVideoConfig,
      interpolate,
      spring,
      Sequence,
      AbsoluteFill,
      Series,
      random,
      Audio,
      Img,
      OffthreadVideo,
      VideoKit,
    );
  } catch (error) {
    console.error('Failed to create dynamic component:', error);
    return null;
  }
}

// Error boundary for catching runtime errors
function ErrorFallback({ error }: { error: string }) {
  return (
    <AbsoluteFill style={{ 
      background: '#1a1a2e', 
      justifyContent: 'center', 
      alignItems: 'center',
      padding: 40,
    }}>
      <div style={{ color: '#ff6b6b', fontSize: 18, textAlign: 'center' }}>
        <p style={{ marginBottom: 10 }}>⚠️ Render Error</p>
        <pre style={{ 
          fontSize: 12, 
          background: 'rgba(255,107,107,0.1)', 
          padding: 20, 
          borderRadius: 8,
          maxWidth: '90%',
          overflow: 'auto',
        }}>
          {error}
        </pre>
      </div>
    </AbsoluteFill>
  );
}

function createErrorComponent(error: string): React.FC {
  const RemotionErrorComponent: React.FC = () => <ErrorFallback error={error} />;
  RemotionErrorComponent.displayName = 'RemotionErrorComponent';
  return RemotionErrorComponent;
}

export function RemotionRenderer({
  title,
  description,
  componentCode,
  durationInFrames,
  fps,
  width,
  height,
  visualizationId,
  displayContext = 'inline',
  renderActions,
  onRetryError,
}: RemotionRendererProps) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isPlaying = true;
  const [speed, setSpeed] = useState(1);
  const [exportState, setExportState] = useState<
    { status: 'idle' }
    | { status: 'starting' }
    | { status: 'rendering'; progress: number; renderId: string; bucketName: string }
    | { status: 'done'; url: string }
    | { status: 'error'; message: string }
  >({ status: 'idle' });
  const renderStartRef = useRef(typeof performance !== 'undefined' ? performance.now() : Date.now());
  const hasTrackedReadyRef = useRef(false);
  const normalizedComponentCode = useMemo(
    () => normalizeRemotionComponentCode(componentCode),
    [componentCode],
  );

  useProductTelemetryOnce(
    'visualization_render_failed',
    Boolean(error),
    {
      tool_name: 'remotion',
      failure_kind: 'runtime-render-error',
      visualization_id: visualizationId,
      display_context: displayContext,
    },
    error ? `${visualizationId ?? 'remotion'}:${error}` : undefined,
  );

  useEffect(() => {
    renderStartRef.current = typeof performance !== 'undefined' ? performance.now() : Date.now();
    hasTrackedReadyRef.current = false;
  }, [componentCode, displayContext, durationInFrames, fps, height, width]);

  // Create the dynamic component
  const DynamicComponent = useMemo(() => {
    setError(null);
    try {
      const component = createDynamicComponent(componentCode);
      if (!component) {
        setError('Failed to compile component');
        return createErrorComponent('Failed to compile component');
      }
      return component;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : String(err);
      setError(errorMsg);
      return createErrorComponent(errorMsg);
    }
  }, [componentCode]);

  useEffect(() => {
    if (error || !DynamicComponent || hasTrackedReadyRef.current) return;

    hasTrackedReadyRef.current = true;
    captureProductTelemetry('visualization_render_ready', {
      tool_name: 'remotion',
      visualization_id: visualizationId,
      display_context: displayContext,
      render_duration_ms: Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) - renderStartRef.current),
      composition_width: width,
      composition_height: height,
      duration_in_frames: durationInFrames,
      fps,
    });
  }, [DynamicComponent, displayContext, durationInFrames, error, fps, height, visualizationId, width]);

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(prev => !prev);
  }, []);

  // --- MP4 export via Remotion Lambda ---
  const handleExportMp4 = useCallback(async () => {
    setExportState({ status: 'starting' });
    try {
      const startRes = await fetch('/api/render/video', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ componentCode: normalizedComponentCode, durationInFrames, fps, width, height, visualizationId }),
      });
      const startData = await startRes.json();
      if (!startRes.ok || !startData.success) {
        throw new Error(startData.error || 'Failed to start render');
      }
      const { renderId, bucketName } = startData;
      setExportState({ status: 'rendering', progress: 0, renderId, bucketName });

      // Poll progress (pass visualizationId so the server persists the MP4 URL on completion)
      const poll = async () => {
        const qs = new URLSearchParams({ renderId, bucketName });
        if (visualizationId) qs.set('visualizationId', visualizationId);
        const res = await fetch(`/api/render/video?${qs.toString()}`);
        const data = await res.json();
        if (!res.ok || !data.success) throw new Error(data.error || 'Progress failed');
        if (data.fatalErrorEncountered) {
          throw new Error(data.errors?.[0]?.message || 'Render failed');
        }
        if (data.done && data.outputFile) {
          setExportState({ status: 'done', url: data.outputFile });
          return;
        }
        setExportState({
          status: 'rendering',
          progress: data.overallProgress ?? 0,
          renderId,
          bucketName,
        });
        setTimeout(poll, 1500);
      };
      await poll();
    } catch (err) {
      setExportState({
        status: 'error',
        message: err instanceof Error ? err.message : 'Export failed',
      });
    }
  }, [normalizedComponentCode, durationInFrames, fps, width, height, visualizationId]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen]);

  const speedControls = (
    <div className="flex items-center gap-1">
      <Gauge weight="bold" className="size-3.5 text-muted-foreground mr-1" />
      {SPEED_OPTIONS.map((s) => (
        <button
          key={s}
          onClick={() => setSpeed(s)}
          className={`px-1.5 py-0.5 rounded text-xs font-medium transition-colors ${
            speed === s
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:text-foreground hover:bg-muted'
          }`}
        >
          {s}x
        </button>
      ))}
    </div>
  );

  const playerElement = (
    <Player
      component={DynamicComponent}
      durationInFrames={durationInFrames}
      fps={fps}
      compositionWidth={width}
      compositionHeight={height}
      style={{
        width: '100%',
        aspectRatio: `${width} / ${height}`,
      }}
      controls
      autoPlay={isPlaying}
      loop
      playbackRate={speed}
    />
  );

  if (error && !DynamicComponent) {
    return (
      <div className="p-4 border border-destructive/50 rounded-lg bg-destructive/10">
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm font-medium text-destructive">Remotion Error</p>
          {onRetryError && (
            <button
              onClick={() => onRetryError({ toolName: 'remotion', error, code: componentCode })}
              className="shrink-0 px-3 py-1 text-xs font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              Retry
            </button>
          )}
        </div>
        <pre className="mt-2 text-xs text-muted-foreground whitespace-pre-wrap">{error}</pre>
      </div>
    );
  }

  return (
    <>
      {/* Normal view. When docked in the artifact panel, drop the card
          border/rounding/bg so the player blends into the panel surface
          instead of looking like a separate object inside it. */}
      <div
        className={cn(
          'w-full overflow-hidden group relative',
          displayContext === 'artifact'
            ? 'border-0 rounded-none bg-transparent'
            : 'border rounded-lg bg-card',
        )}
      >
        <div
          className={cn(
            'flex items-center justify-between px-4 py-2 gap-3',
            displayContext === 'artifact' ? '' : 'border-b bg-muted/30',
          )}
        >
          {/* When docked in the artifact panel, the panel header already
              shows the title — emitting it here too creates a stacked /
              wrapped repetition. Show title only in inline contexts. */}
          {displayContext === 'artifact' ? (
            <div className="min-w-0" />
          ) : (
            <div className="min-w-0 flex-1">
              <h3 className="font-medium text-sm truncate">{title}</h3>
              {description && (
                <p className="text-xs text-muted-foreground truncate">{description}</p>
              )}
            </div>
          )}
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs text-muted-foreground tabular-nums">
              {formatDuration(durationInFrames, fps)} @ {fps}fps
            </span>
            <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-2">
              {renderActions}
              {speedControls}
              {exportState.status === 'idle' && (
                <Button variant="ghost" size="sm" onClick={handleExportMp4} title="Render as MP4">
                  <Download weight="bold" className="size-4" />
                  <span className="ml-1 text-xs">Export MP4</span>
                </Button>
              )}
              {exportState.status === 'starting' && (
                <Button variant="ghost" size="sm" disabled>
                  <Spinner weight="bold" className="size-4 animate-spin" />
                  <span className="ml-1 text-xs">Starting…</span>
                </Button>
              )}
              {exportState.status === 'rendering' && (
                <Button variant="ghost" size="sm" disabled>
                  <Spinner weight="bold" className="size-4 animate-spin" />
                  <span className="ml-1 text-xs">{Math.round(exportState.progress * 100)}%</span>
                </Button>
              )}
              {exportState.status === 'done' && (
                <>
                  <Button variant="ghost" size="sm" asChild>
                    <a href={exportState.url} target="_blank" rel="noopener noreferrer" download>
                      <Download weight="bold" className="size-4" />
                      <span className="ml-1 text-xs">Download MP4</span>
                    </a>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleExportMp4}
                    title="Render a new MP4 from the current video"
                  >
                    <ArrowClockwise weight="bold" className="size-4" />
                    <span className="ml-1 text-xs">Re-export</span>
                  </Button>
                </>
              )}
              {exportState.status === 'error' && (
                <Button variant="ghost" size="sm" onClick={handleExportMp4} title={exportState.message}>
                  <Download weight="bold" className="size-4 text-destructive" />
                  <span className="ml-1 text-xs text-destructive">Retry</span>
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleFullscreen}
              >
                <ArrowsOut weight="bold" className="size-4" />
                <span className="ml-1 text-xs">Fullscreen</span>
              </Button>
            </div>
          </div>
        </div>
        
        <div className="w-full bg-slate-900">
          {playerElement}
        </div>
      </div>

      {/* Fullscreen overlay */}
      {isFullscreen && (
        <div className="fixed inset-0 z-50 bg-slate-900 flex flex-col">
          <div className="flex items-center justify-between px-6 py-4 bg-slate-900/90 backdrop-blur-sm border-b border-slate-700">
            <div>
              <h2 className="font-semibold text-lg text-white">{title}</h2>
              {description && (
                <p className="text-sm text-slate-400">{description}</p>
              )}
            </div>
            <div className="flex items-center gap-3">
              {renderActions}
              {speedControls}
              <Button
                variant="outline"
                size="sm"
                onClick={toggleFullscreen}
                className="bg-slate-800 border-slate-700 text-white hover:bg-slate-700"
              >
                <X weight="bold" className="size-4 mr-1" />
                Close
                <span className="text-xs text-slate-400 ml-2">(Esc)</span>
              </Button>
            </div>
          </div>
          
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="max-w-5xl w-full">
              {playerElement}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/**
 * Renders remotion output from a tool call
 */
export function RemotionToolOutput({ output, visualizationId, displayContext = 'inline', renderActions, onRetryError }: {
  output: {
    success: boolean;
    title?: string;
    description?: string;
    componentCode?: string;
    durationInFrames?: number;
    fps?: number;
    width?: number;
    height?: number;
    error?: string;
  };
  visualizationId?: string;
  displayContext?: 'inline' | 'artifact';
  renderActions?: React.ReactNode;
  onRetryError?: (info: { toolName: string; error: string; code: string }) => void;
}) {
  if (!output.success) {
    return (
      <div className="p-4 border border-amber-500/50 rounded-lg bg-amber-500/10">
        <p className="text-sm font-medium text-amber-600 dark:text-amber-400">Remotion Error</p>
        <pre className="mt-2 text-xs text-muted-foreground whitespace-pre-wrap">{output.error}</pre>
      </div>
    );
  }

  if (!output.componentCode) {
    return null;
  }

  return (
    <RemotionRenderer
      title={output.title || 'Video'}
      description={output.description}
      componentCode={output.componentCode}
      durationInFrames={output.durationInFrames || 120}
      fps={output.fps || 30}
      width={output.width || 1280}
      height={output.height || 720}
      visualizationId={visualizationId}
      displayContext={displayContext}
      renderActions={renderActions}
      onRetryError={onRetryError}
    />
  );
}
